import {
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldOrigin,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import { ZonesEditorSurface } from 'features/libra/stepComponents/zoneAnalysis/components/ZonesEditorSurface'
import {
  parseZonesFromFieldValues,
  reconcileZoneSnapshot,
  useStableZoneSnapshot,
  ZoneChangeGuard,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { validateZoneGeometryChanges } from 'features/libra/stepComponents/zoneAnalysis/zoneGeometryValidation'
import {
  patchZoneById,
  removeZoneById,
  replaceImageZoneProposals,
} from 'features/libra/stepComponents/zoneAnalysis/zoneMutations'
import { zoneMutationOrigin } from 'features/libra/stepComponents/zoneAnalysis/zoneOrigin'
import {
  hasDuplicateStoredZoneIds,
  hasEmptyStoredZoneId,
  prepareZonesForSave,
} from 'features/libra/stepComponents/zoneAnalysis/zoneSave'
import type { FieldValuesSetter } from 'features/libra/ticket/fieldOrigin'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { RolodexPermissionsValues } from 'types'

import { ZonesSection } from './zonesSectionTypes'

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasLegacyZoneRows(rawZones: unknown): boolean {
  return (
    Array.isArray(rawZones) &&
    rawZones.some((zone) => isRecord(zone) && zone.id === undefined)
  )
}

export interface ZonesSectionViewProps {
  config: ZonesSection
  fieldValues: FieldValues
  /** Immutable ticket snapshot used to detect newly introduced geometry. */
  initialFieldValues?: FieldValues
  drawingResetKey?: number
  /**
   * ConditionalForm's staged field-value setter. Undefined in readonly mode
   * (the step renders without editing affordances).
   */
  setFieldValues?: FieldValuesSetter
}

/**
 * Embeds the shared {@link ZonesEditorSurface} inside a ConditionalForm step,
 * backed by the step's own staged field-value state (so it rides the step's
 * single save banner — no separate provider or banner).
 *
 * Draft semantics mirror the standalone `ZoneAnalysisDataProvider`: AI
 * `ai_suggested` proposals are kept in LOCAL state and never written to the
 * field value, so they neither persist nor mark the step dirty until the SME
 * confirms them. Only reviewed (confirmed/edited/hand-drawn) zones are written
 * back through `setFieldValues`.
 */
export const ZonesSectionView: FC<ZonesSectionViewProps> = (props) => {
  const { ticketId } = useTicketAccess()
  const scopeKey = JSON.stringify([
    ticketId ?? null,
    props.config.zoneFieldName,
  ])
  const scopeRef = useRef<string | undefined>(scopeKey)
  scopeRef.current = scopeKey
  useEffect(() => {
    scopeRef.current = scopeKey
    return () => {
      if (scopeRef.current === scopeKey) scopeRef.current = undefined
    }
  }, [scopeKey])
  return (
    <ZonesSectionViewContent
      key={scopeKey}
      {...props}
      scopeKey={scopeKey}
      scopeRef={scopeRef}
    />
  )
}

const ZonesSectionViewContent: FC<
  ZonesSectionViewProps & {
    scopeKey: string
    scopeRef: MutableRefObject<string | undefined>
  }
> = ({
  config,
  fieldValues,
  initialFieldValues,
  drawingResetKey,
  setFieldValues,
  scopeKey,
  scopeRef,
}) => {
  const {
    ticketTags,
    readOnly: ticketReadOnly,
    isLoading: ticketLoading,
    ticketId,
  } = useTicketAccess()
  const unitId = ticketTags.unit as string | undefined
  const readonly = !setFieldValues || ticketReadOnly || ticketLoading
  const readonlyRef = useRef(readonly)
  readonlyRef.current = readonly

  const zoneFieldName = config.zoneFieldName
  const baselineFieldValues = initialFieldValues ?? fieldValues
  // Reviewed zones persisted on the ticket (via the step's staged field values).
  const baselineSnapshot = useStableZoneSnapshot(
    baselineFieldValues,
    zoneFieldName,
    ticketId,
    undefined,
    drawingResetKey
  )
  const baselineZones = baselineSnapshot.zones
  const persistedSnapshot = useStableZoneSnapshot(
    fieldValues,
    zoneFieldName,
    ticketId,
    baselineSnapshot,
    drawingResetKey
  )
  const persistedZones = persistedSnapshot.zones
  const persistedZonesRef = useRef(persistedZones)
  persistedZonesRef.current = persistedZones
  const persistedSnapshotRef = useRef(persistedSnapshot)
  persistedSnapshotRef.current = persistedSnapshot

  // Unreviewed AI proposals live here only — never in the field value.
  const [drafts, setDrafts] = useState<ZoneParams[]>([])

  // Displayed = persisted reviewed zones + still-pending drafts. Dedupe by id
  // so a just-confirmed draft (now present in persistedZones) isn't shown twice.
  const displayed = useMemo(() => {
    const persistedIds = new Set(persistedZones.map((z) => z.id))
    const pendingDrafts = drafts.filter(
      (d) => d.ai_suggested && !persistedIds.has(d.id)
    )
    return [...persistedZones, ...pendingDrafts]
  }, [persistedZones, drafts])

  useEffect(() => {
    const persistedIds = new Set(persistedZones.map((zone) => zone.id))
    setDrafts((current) => {
      const next = current.filter((zone) => !persistedIds.has(zone.id))
      return next.length === current.length ? current : next
    })
  }, [persistedZones])

  const commitReviewed = useCallback(
    (
      update: (zones: ZoneParams[]) => ZoneParams[],
      forceValidateIds: readonly string[] = [],
      origin?: FieldOrigin
    ) => {
      const currentSnapshot = persistedSnapshotRef.current
      if (
        !setFieldValues ||
        readonlyRef.current ||
        scopeRef.current !== scopeKey ||
        baselineSnapshot.parseError ||
        currentSnapshot.parseError ||
        hasDuplicateStoredZoneIds(currentSnapshot.rawZones) ||
        hasEmptyStoredZoneId(currentSnapshot.rawZones)
      )
        return
      setFieldValues((prev) => {
        if (scopeRef.current !== scopeKey) return prev
        const parsed = parseZonesFromFieldValues(prev, zoneFieldName)
        const currentRawZones = prev[zoneFieldName]?.value
        if (
          parsed.parseError ||
          baselineSnapshot.parseError ||
          hasDuplicateStoredZoneIds(currentRawZones) ||
          hasEmptyStoredZoneId(currentRawZones)
        )
          return prev
        const snapshot = persistedSnapshotRef.current
        if (
          snapshot.ticketId !== ticketId ||
          snapshot.fieldName !== zoneFieldName ||
          snapshot.resetKey !== drawingResetKey
        )
          return prev
        const reconciled = reconcileZoneSnapshot(
          snapshot.rawZones,
          snapshot.zones,
          currentRawZones,
          parsed.zones
        )
        if (
          reconciled.hasAmbiguousLegacyChange &&
          hasLegacyZoneRows(currentRawZones)
        )
          return prev
        const currentZones = reconciled.zones
        const nextZones = update(currentZones)
        if (
          isEqual(nextZones, currentZones) ||
          validateZoneGeometryChanges({
            baselineZones,
            proposedZones: nextZones,
            forceValidateIds,
          })
        )
          return prev
        // Marked here rather than via the setter's origin option, so a rejected
        // update leaves the staged mark untouched.
        const savedZones = prepareZonesForSave({
          rawZones: currentRawZones,
          previousZones: currentZones,
          latestZones: currentZones,
          proposedZones: nextZones,
        })
        const wrapper = omit(
          {
            ...(prev[zoneFieldName] ?? wrapFieldValue(savedZones)),
            value: savedZones,
          },
          ['updateSource', 'updatedAt', 'aiExtracted', 'documentsSearched']
        )
        return {
          ...prev,
          [zoneFieldName]: origin ? withFieldOrigin(wrapper, origin) : wrapper,
        }
      })
    },
    [
      baselineZones,
      baselineSnapshot,
      drawingResetKey,
      scopeKey,
      scopeRef,
      setFieldValues,
      ticketId,
      zoneFieldName,
    ]
  )

  const addZone = useCallback(
    (zone: ZoneParams) =>
      commitReviewed((zones) =>
        zones.some((item) => item.id === zone.id) ? zones : [...zones, zone]
      ),
    [commitReviewed]
  )

  const changeZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>, guard?: ZoneChangeGuard) => {
      const update = (zones: ZoneParams[]) =>
        patchZoneById(zones, zoneId, updates, guard)
      if (readonlyRef.current) return
      setDrafts(update)
      // A tab pre-fill makes the AI the last writer; any other edit is a person's.
      const current = persistedZonesRef.current
      commitReviewed(update, [], zoneMutationOrigin(current, update(current)))
    },
    [commitReviewed]
  )

  const removeZone = useCallback(
    (zoneId: string) => {
      if (readonlyRef.current) return
      const update = (zones: ZoneParams[]) => removeZoneById(zones, zoneId)
      setDrafts(update)
      commitReviewed(update)
    },
    [commitReviewed]
  )

  const confirmProposal = useCallback(
    (proposal: ZoneParams) => {
      if (readonlyRef.current) return
      commitReviewed(
        (zones) => {
          const current = zones.find((zone) => zone.id === proposal.id)
          if (current) {
            return current.ai_suggested
              ? zones.map((zone) =>
                  zone.id === proposal.id
                    ? { ...zone, ai_suggested: false }
                    : zone
                )
              : zones
          }
          return [...zones, { ...proposal, ai_suggested: false }]
        },
        [proposal.id]
      )
    },
    [commitReviewed]
  )

  const replaceImageProposals = useCallback(
    (imageId: string, proposals: ZoneParams[]) => {
      if (readonlyRef.current) return
      setDrafts((current) =>
        replaceImageZoneProposals(current, imageId, proposals)
      )
    },
    []
  )

  if (!unitId) {
    return (
      <EmptyState>
        No unit specified for this ticket. Please ensure the ticket is tagged
        with a unit.
      </EmptyState>
    )
  }

  return (
    <ZonesEditorSurface
      zones={displayed}
      mutationBaselineZones={baselineZones}
      onAddZone={addZone}
      onChangeZone={changeZone}
      onRemoveZone={removeZone}
      onConfirmProposal={confirmProposal}
      onReplaceImageProposals={replaceImageProposals}
      unitId={unitId}
      agentSectionId={config.id}
      zoneAgentPermissions={
        (config.agentPermissions ?? []) as RolodexPermissionsValues[]
      }
      visibleTabs={config.visibleTabs}
      showCoverageBreakdown={false}
      readonly={readonly}
      isLoading={ticketLoading}
      drawingResetKey={drawingResetKey}
    />
  )
}
